<template>
  <div class="material-form">
    <form @submit.stop.prevent="form.handleSubmit()">
      <form.Field
        name="name"
        :validators="{
          onBlur: yup.string().required('Name is required')
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="(field.state.value as string)"
            label="Name"
            :name="field.name"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field name="description" >
        <template #default="{ field }">
          <BaseTextArea
            v-model="(field.state.value as string)"
            label="Description"
            :name="field.name"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        name="cost"
        :validators="{
          onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Cost must be a positive number',(value) => !isNaN(Number(value)) && Number(value) > 0).required('Cost is required')
        }"
      >
        <template #default="{ field }">
          <BaseInputCurrency
            :model-value="(field.state.value as string)"
            :name="field.name"
            label="Cost"
            :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
            @blur="field.handleBlur"
            @input="(e) => field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <h6 class="mt-4 font-semibold">Image</h6>
      <BaseButton
        variant="secondary"
        text="Upload Image"
        type="button"
        icon-class="i-mdi-paperclip"
        class="mt-2"
        :class="{ 'cursor-not-allowed opacity-50': materialName === '' }"
        :disabled="materialName === ''"
        @click="open = true"
      />

      <div
        v-if="material.image"
        class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
      >
        <img
          :src="material.image"
          class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
        >
        <span
          class="absolute top-0 right-0 p-1 flex justify-center items-center rounded-full border-transparent bg-black/50 hover:bg-black/75 transition duration-200 ease-in-out cursor-pointer overflow-hidden select-none size-[1.3rem]"
          @click="deleteAttachment"
        >
          <span class="i-mdi-close text-white"></span>
        </span>
      </div>

      <fieldset class="mt-6">
        <legend class="text-lg font-semibold">Brand</legend>

        <form.Field
          name="brand.name"
        >
          <template #default="{ field }">
            <BaseInput
              v-model="(field.state.value as string)"
              label="Name"
              :name="field.name"
              @blur="field.handleBlur"
              @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Field
          name="brand.logoURL"
          :validators="{
            onBlur: yup.string().url('Logo URL must be a valid URL')
          }"
        >
          <template #default="{ field }">
            <BaseInput
              v-model="(field.state.value as string)"
              label="Logo URL"
              :name="field.name"
              :error="field.state.meta.errors && field.state.meta.errors[0] as string"
              @blur="field.handleBlur"
              @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

      </fieldset>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ListMaterials' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>

    <Modal
      v-model="open"
      title="Upload Files"
      width="w-full md:w-1/2"
      close-button-text="Close"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      @close="setMaterialImage"
    >
      <template #body>
        <div class="mt-4">
          <AppFileUpload
            :storage-path="`materials/documents/${materialName}-attachments/`"
            @on-file-upload-completed="fileUploadCompleted"
          />
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { Material } from '@/models/material'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import BaseTextArea from '@/components/base/BaseTextArea.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import Modal from '@/components/app/AppModal.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { ref, PropType } from 'vue'
import StorageService from '@/services/StorageService'
import { useNotification } from '@kyvg/vue3-notification'
import { toNumber } from '@/utils'

const props = defineProps({
  model: {
    type: Object as PropType<Material>,
    default: () => ({})
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const material = ref({ ...props.model, cost: props.model.cost?.toString() })
const open = ref(false)
const storageService = new StorageService()
const { notify } = useNotification()

const form = useForm({
  defaultValues: material.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const material = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined))  as unknown as Material
    material.image = tempImage.value
    material.cost = toNumber(material.cost)
    await props.saveFunction(material)
  }
})
const materialName = form.useStore((state) => state.values.name)
const tempImage = ref(material.value.image)

const fileUploadCompleted = (attachments) => {
  if (attachments.length > 0) {
    tempImage.value = attachments[0]
  } else {
    tempImage.value = material.value.image
  }
}

const setMaterialImage = async () => {
  if (tempImage.value) {
    if (material.value.image) {
      if (material.value.image !== tempImage.value) {
        try {
          await storageService.deleteAttachmentWithStyledImages(material.value.image)
          material.value.image = tempImage.value
        } catch (error) {
          console.error(error)
          notify({
            type: 'error',
            title: 'Error',
            text: 'An error occurred while uploading the attachment'
          })
        }
      }
    } else {
      material.value.image = tempImage.value
    }
  }
}

const deleteAttachment = async () => {
  try {
    await storageService.deleteAttachmentWithStyledImages(material.value.image)
    material.value.image = ''
    tempImage.value = ''
  } catch (error) {
    console.error(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'An error occurred while deleting the attachment'
    })
  }
}

</script>
